/**
 * EventCard Component
 * 
 * Displays event information with comprehensive icon system for visual scanning.
 * Used in HomePage "This Week" section and Activity EventsTab.
 * 
 * Features:
 * - RSVP status indicators with visual feedback
 * - Event timing icons (starting soon, live, ended)
 * - Registration status with capacity indicators
 * - Distance and location metadata
 * - Enhanced visual hierarchy with semantic icons
 * 
 * Issue #411: Comprehensive EventCard icon system implementation
 * Design: ORO Nightfall semantic token system (Layer 2)
 */

import { Calendar, MapPin, Users, Clock, CheckCircle, XCircle, AlertCircle, Timer, Lock } from 'lucide-react';
import { useState } from 'react';
import { EventDateTime } from './ui/DateTimeDisplay';
import Badge from './Badge';
import { useMe } from '../hooks/useMe';
import { getEventTimingStatus } from '../lib/datetime';
import { formatDistance } from '../lib/units';

export interface Event {
  id: string;
  short_code: string; // Issue #729: Human-friendly URL identifier
  title: string;
  date: string; // Will be deprecated in favor of starts_at timestamp
  starts_at?: number; // Unix timestamp in seconds
  ends_at?: number; // Unix timestamp in seconds
  location: string;
  attendees: number;
  imageUrl: string;
  distance_m?: number; // Distance in meters (canonical unit - Issue #1083)
  capacity?: number; // Maximum event capacity
  rsvp_count?: number; // Number of RSVPs
  rsvp_status?: 'yes' | 'no' | 'maybe' | null; // #2089: Authed user's RSVP status (matches shared EventWithRSVP.rsvp_status — was user_rsvp_status, renamed to align)
  is_full?: boolean; // Whether event is at capacity
  registration_required?: boolean; // Whether registration is required
  visibility?: 'public' | 'members_only'; // Event visibility (Issue #427)
  training_group_id?: string; // Linked training group ID (Issue #754)
  training_group_name?: string | null; // #2089: Training group name for badge display (null when event isn't linked to a TG)
  event_number?: number | null; // #1509: Sequential run number
}

interface EventCardProps {
  event: Event;
  onClick?: () => void;
}

/**
 * Get RSVP status styling and icon
 */
function getRSVPStatusDisplay(status?: 'yes' | 'no' | 'maybe' | null) {
  switch (status) {
    case 'yes':
      return {
        icon: CheckCircle,
        color: 'var(--oro-success)',
        label: 'Going',
        bgColor: 'var(--oro-success-soft)'
      };
    case 'maybe':
      return {
        icon: AlertCircle,
        color: 'var(--oro-warning)',
        label: 'Maybe',
        bgColor: 'var(--oro-warning-soft)'
      };
    case 'no':
      return {
        icon: XCircle,
        color: 'var(--text-muted)',
        label: 'Not going',
        bgColor: 'transparent'
      };
    default:
      return null;
  }
}

/**
 * Simple image component with fallback for broken images.
 *
 * #1963: `loading="lazy"` defers off-screen image loads; explicit
 * width/height pin a 2:1 aspect ratio so the browser reserves layout
 * space before the image arrives (prevents CLS). While the network
 * image is pending, render an `animate-pulse` skeleton over the
 * existing oro-surface-alt parent slot — mirrors the live-indicator
 * pulse already in use at line 194. The fallback image is local, so
 * no skeleton is needed there.
 */
function ImageWithFallback({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // #2260: DOM-based fallback (ClubCard's pattern) — the referenced
  // /images/event_fallback.jpg never shipped, so a broken event image
  // degraded to the browser broken-image glyph.
  if (error || !src) {
    return (
      <div
        className={`${className ?? ''} flex items-center justify-center`}
        style={{ backgroundColor: 'var(--oro-surface-alt)' }}
        role="img"
        aria-label="Event image unavailable"
      >
        <Calendar
          className="w-8 h-8"
          style={{ color: 'var(--text-muted)' }}
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse"
          style={{ backgroundColor: 'var(--oro-surface-alt)' }}
          aria-hidden="true"
        />
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        width={256}
        height={128}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </>
  );
}

export default function EventCard({ event, onClick }: EventCardProps) {
  // Get user's distance unit preference (Issue #1083)
  const { data: meData } = useMe();
  const distanceUnit = meData?.distance_unit || 'mi';

  const timingStatus = getEventTimingStatus(event.starts_at, event.ends_at);
  const rsvpStatus = getRSVPStatusDisplay(event.rsvp_status);
  const isNearCapacity = event.capacity && event.rsvp_count && (event.rsvp_count / event.capacity >= 0.8);
  const isFull = event.is_full || (event.capacity && event.rsvp_count && event.rsvp_count >= event.capacity);

  return (
    <div
      className="shrink-0 w-full sm:w-64 rounded-2xl overflow-hidden cursor-pointer transition-colors"
      style={{
        backgroundColor: 'var(--oro-surface)',
        border: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-card)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-gold)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--oro-border)';
      }}
      onClick={onClick}
    >
      {/* Image with overlay indicators */}
      <div className="relative h-32" style={{ backgroundColor: 'var(--oro-surface-alt)' }}>
        <ImageWithFallback
          src={event.imageUrl}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        
        {/* Timing status badge */}
        {timingStatus.status !== 'scheduled' && (
          <div 
            className="absolute top-2 left-2 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1"
            style={{
              backgroundColor: timingStatus.status === 'live' 
                ? 'var(--oro-success)' 
                : timingStatus.status === 'starting-soon'
                ? 'var(--oro-warning)'
                : 'var(--text-muted)',
              color: timingStatus.status === 'ended' ? 'var(--oro-bg)' : 'var(--oro-bg)'
            }}
          >
            {timingStatus.status === 'live' && <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
            {timingStatus.status === 'starting-soon' && <Timer className="w-3 h-3" />}
            <span>{timingStatus.label}</span>
          </div>
        )}

        {/* Capacity warning indicator */}
        {(isNearCapacity || isFull) && (
          <div 
            className="absolute top-2 right-2 p-1.5 rounded-full"
            style={{
              backgroundColor: isFull ? 'rgba(231, 76, 60, 0.9)' : 'rgba(255, 193, 7, 0.9)'
            }}
          >
            <AlertCircle
              className="w-3 h-3"
              style={{ color: isFull ? 'var(--text-primary)' : 'var(--oro-bg)' }}
            />
          </div>
        )}

        {/* RSVP status indicator */}
        {rsvpStatus && (
          <div 
            className="absolute bottom-2 right-2 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1"
            style={{
              backgroundColor: rsvpStatus.bgColor || 'rgba(0, 0, 0, 0.6)',
              color: rsvpStatus.color,
              backdropFilter: 'blur(4px)'
            }}
          >
            <rsvpStatus.icon className="w-3 h-3" />
            <span>{rsvpStatus.label}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base font-medium mb-2 line-clamp-2" style={{ color: 'var(--text-primary)' }}>
          {event.title}
        </h3>

        {/* Event Number + Training Group Badges */}
        {(event.event_number != null || event.training_group_name) && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {event.event_number != null && (
              <Badge variant="accent" size="sm">#{event.event_number}</Badge>
            )}
            {event.training_group_name && (
              <Badge variant="subtle" size="sm" className="max-w-[160px] inline-flex">
                <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{event.training_group_name}</span>
              </Badge>
            )}
          </div>
        )}

        <div className="space-y-2">
          {/* Date/Time */}
          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            <Calendar className="w-4 h-4 shrink-0" />
            {event.starts_at ? (
              <EventDateTime 
                timestamp={event.starts_at}
                endTimestamp={event.ends_at}
                className="text-[13px]"
              />
            ) : (
              <span>{event.date}</span>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-muted)' }}>
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>

          {/* Distance - Issue #1083: distance_m in meters, format with user preference */}
          {event.distance_m && event.distance_m > 0 && (
            <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{formatDistance(event.distance_m, distanceUnit)}</span>
            </div>
          )}

          {/* Members-only indicator (Issue #427) */}
          {event.visibility === 'members_only' && (
            <div className="flex items-center gap-1.5 text-[13px]" style={{ color: 'var(--text-muted)' }}>
              <Lock className="w-3 h-3 shrink-0" aria-label="Members-only" />
              <span className="text-xs">Members</span>
            </div>
          )}

          {/* Attendees with capacity info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[13px]" style={{ 
              color: isFull ? 'var(--oro-warning)' : 'var(--oro-gold)' 
            }}>
              <Users className="w-4 h-4 shrink-0" />
              <span>
                {event.rsvp_count || event.attendees} 
                {event.capacity && ` / ${event.capacity}`}
                {isFull ? ' (Full)' : ' attending'}
              </span>
            </div>
            
            {/* Registration indicator */}
            {event.registration_required && (
              <div className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: 'var(--oro-surface-alt)', 
                  color: 'var(--text-muted)' 
                }}
              >
                <Clock className="w-3 h-3" />
                <span>RSVP</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
